import { useState, useRef, useEffect } from 'react';
import { registerModalBackHandler } from '../../lib/native';

interface LegalModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export function LegalModal({ open, onClose, title, children }: LegalModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  useEffect(() => {
    if (open) setScrolledToBottom(false);
  }, [open]);

  // Android hardware back button closes this modal instead of navigating
  // the route underneath it.
  useEffect(() => {
    if (!open) return;
    return registerModalBackHandler(onClose);
  }, [open, onClose]);

  function handleScroll() {
    const el = contentRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      setScrolledToBottom(true);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div
        className="bg-app-card border border-app-border rounded-2xl shadow-xl w-full max-w-2xl max-h-dvh-85 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-app-border shrink-0">
          <h2 className="text-lg font-semibold text-app-fg">{title}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-black/5 transition-colors sk-hit"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-5 legal-content"
        >
          {children}
        </div>

        <div className="px-6 py-4 border-t border-app-border shrink-0">
          <button
            onClick={onClose}
            className="w-full h-10 rounded-xl bg-gold text-ink text-sm font-semibold hover:brightness-105 transition-all sk-hit"
          >
            {scrolledToBottom ? 'Close' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
}
